import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { hashKey } from "@tanstack/query-core";
import { Suspense, useEffect, useRef } from "react";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { PeopleReturnListTracker } from "@/components/people/PeopleReturnListTracker";
import { RouteAnnouncer } from "@/lib/shared-ui";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, Outlet } from "react-router-dom";
import { SetupRoute } from "@/components/layout/SetupRoute";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { AdminRoute } from "@/components/layout/AdminRoute";
import { OrgAdminRoute } from "@/components/layout/OrgAdminRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { CommunityLayout } from "@/components/community/CommunityLayout";
import { PEOPLE_ALL_PATH, PEOPLE_ENGAGEMENT_SETTINGS_PATH, PEOPLE_SETTINGS_PATH } from "@/components/community/peopleSubNav";
import { LegacyPathRedirect } from "@/components/people/LegacyPathRedirect";
import { StaffRoute } from "@/components/layout/StaffRoute";
import { TaskNotificationGate } from "@/components/tasks/TaskNotificationGate";
import { StaffAccessGate } from "@/components/layout/StaffAccessGate";
import { ModuleRouteGate } from "@/components/layout/ModuleRouteGate";
import { FinanceSettingsIndex } from "@/components/settings/FinanceSettingsIndex";
import { QboFinanceRouteGate } from "@/components/layout/QboFinanceRouteGate";
import { TierRouteGate } from "@/components/layout/TierRouteGate";
import {
  SchoolAcademicRoute,
  SchoolAttendanceRoute,
  SchoolChildcareRoute,
  SchoolFinanceRoute,
  SchoolHealthRoute,
} from "@/components/schools/SchoolsRouteGates";
import { CapabilityRouteGate } from "@/components/capabilities/CapabilityGate";
import { MemberCapabilityRoute } from "@/components/capabilities/MemberCapabilityRoute";
import { getTenantQueryKey } from "@/lib/tenant-query-key";
import { LoadingSkeleton } from "@/components/record-detail/states/LoadingSkeleton";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { MemberShell } from "@/components/layout/MemberShell";
import { VolunteerShell } from "@/components/layout/VolunteerShell";
import { UserScopedQueryClientProvider } from "@/lib/user-scoped-query-client";
import { reloadOnSignedInUserChange } from "@/lib/reload-on-user-change";
import { GlobalErrorBoundary } from "@/components/errors/GlobalErrorBoundary";
import { AppShell } from "@/ui/app-shell/AppShell";
import { preloadRelatedRoutes } from "@/lib/lazy-routes";
import { recordRouteRender } from "@/lib/spa-navigation-tracker";
import { SettingsLayout } from "@/components/settings/SettingsLayout";
import { SettingsSectionLayout } from "@/components/settings/SettingsSectionLayout";
import { SettingsIndexRedirect } from "@/components/settings/SettingsIndexRedirect";
const SettingsPageLazy = lazyWithRetry(() => import("./pages/SettingsPage"));
const DirectoryAdminSettings = lazyWithRetry(() => import("./pages/DirectoryAdminSettings"));
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { RoutePolicyBoundary } from "@/components/layout/RoutePolicyBoundary";
import { isRouteErrorBoundaryExempt, resolveRoutePolicy } from "@/lib/route-policies";
import { RouteErrorFallback } from "@/components/layout/RouteErrorFallback";
import { platform } from "@/lib/platform";
import { useDeepLinks } from "@/hooks/useDeepLinks";
import { useNativePush } from "@/hooks/useNativePush";
import { usePublicForceLightActive } from "@/lib/public-force-light-store";
import { useForcedLightTheme } from "@/hooks/useForcedLightTheme";
import { GENERAL_CHECK_IN_PATH, KIDS_CHECK_IN_PATH, KIOSK_PATH, isKioskPathname, isTouchStationPathname } from "@/lib/kiosk-route";
import { useTouchSurface } from "@/hooks/useTouchSurface";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { hideSplash } from "@/lib/splash";
import { ContributionImportRedirect } from "@/components/contributions/ContributionImportRedirect";
import { WelcomeFlow, WelcomeFlowProvider } from "@/components/onboarding/WelcomeFlow";
import { ChangelogModal, ChangelogProvider } from "@/components/changelog/ChangelogModal";
import {
  AuthenticatedProviders,
  AuthenticatedRouteProviders,
} from "@/components/layout/AuthenticatedProviders";

// Only Auth is eagerly loaded (initial landing page / LCP critical)
import Auth from "./pages/Auth";

// All other pages are lazy-loaded to reduce initial bundle size
const Index = lazyWithRetry(() => import("./pages/Index"));
const HomeRedirect = lazyWithRetry(() => import("./pages/HomeRedirect"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const FinanceIntegration = lazyWithRetry(() => import("./pages/FinanceIntegration"));
const IntegrationSettings = lazyWithRetry(() => import("./pages/IntegrationSettings"));
const EmailDomainSetup = lazyWithRetry(() => import("./pages/EmailDomainSetup"));
const Tasks = lazyWithRetry(() => import("./pages/Tasks"));
const FinanceAuditLog = lazyWithRetry(() => import("./pages/FinanceAuditLog"));
const FinanceQueue = lazyWithRetry(() => import("./pages/FinanceQueue"));
const BankingHome = lazyWithRetry(() => import("./pages/finance/BankingHome"));
const Deposits = lazyWithRetry(() => import("./pages/finance/Deposits"));
const DepositDetail = lazyWithRetry(() => import("./pages/finance/DepositDetail"));
const PostingLog = lazyWithRetry(() => import("./pages/finance/PostingLog"));
const JournalViewer = lazyWithRetry(() => import("./pages/finance/JournalViewer"));
const AccountsPayable = lazyWithRetry(() => import("./pages/finance/AccountsPayable"));
const AccountsReceivable = lazyWithRetry(() => import("./pages/finance/AccountsReceivable"));
const Budgets = lazyWithRetry(() => import("./pages/finance/Budgets"));
const MonthEndClose = lazyWithRetry(() => import("./pages/finance/MonthEndClose"));
const AccountingSetup = lazyWithRetry(() => import("./pages/finance/AccountingSetup"));
const ContributionDefaultsSettings = lazyWithRetry(() => import("./pages/finance/ContributionDefaultsSettings"));
const ApprovalPolicies = lazyWithRetry(() => import("./pages/finance/ApprovalPolicies"));
import { FinanceSettingsLayout } from "@/components/finance/FinanceSettingsLayout";
const FundTransfers = lazyWithRetry(() => import("./pages/finance/FundTransfers"));
const DepreciationSchedule = lazyWithRetry(() => import("./pages/finance/DepreciationSchedule"));
const PurchaseOrders = lazyWithRetry(() => import("./pages/finance/PurchaseOrders"));
const PayrollIntegration = lazyWithRetry(() => import("./pages/finance/PayrollIntegration"));
const FinanceDashboardPage = lazyWithRetry(() => import("./pages/finance/FinanceDashboard"));
const Vendor1099 = lazyWithRetry(() => import("./pages/finance/Vendor1099"));
const Form990Export = lazyWithRetry(() => import("./pages/finance/Form990Export"));
const HousingAllowance = lazyWithRetry(() => import("./pages/finance/HousingAllowance"));
const RestrictedReleases = lazyWithRetry(() => import("./pages/finance/RestrictedReleases"));
const ReleaseWizard = lazyWithRetry(() => import("./pages/finance/ReleaseWizard"));
const ManualJournalEntry = lazyWithRetry(() => import("./pages/finance/ManualJournalEntry"));
const RecurringJournals = lazyWithRetry(() => import("./pages/finance/RecurringJournals"));
const RecurringExpenses = lazyWithRetry(() => import("./pages/finance/RecurringExpenses"));
const PayablesSettings = lazyWithRetry(() => import("./pages/finance/PayablesSettings"));
const AllocationRules = lazyWithRetry(() => import("./pages/finance/AllocationRules"));
const EndowmentManagement = lazyWithRetry(() => import("./pages/finance/EndowmentManagement"));
const ConditionalContributions = lazyWithRetry(() => import("./pages/finance/ConditionalContributions"));
const InKindContributions = lazyWithRetry(() => import("./pages/finance/InKindContributions"));
const PledgeDiscounting = lazyWithRetry(() => import("./pages/finance/PledgeDiscounting"));
const RecurringRulesSettings = lazyWithRetry(() => import("./pages/finance/RecurringRulesSettings"));
const BankConnectionsSettings = lazyWithRetry(() => import("./pages/finance/BankConnectionsSettings"));
const PreGoLiveCleanup = lazyWithRetry(() => import("./pages/finance/PreGoLiveCleanup"));
const IntegrationsSettings = lazyWithRetry(() => import("./pages/finance/IntegrationsSettings"));
const AuditTrail = lazyWithRetry(() => import("./pages/finance/AuditTrail"));
const ChartOfAccounts = lazyWithRetry(() => import("./pages/finance/ChartOfAccounts"));
const GeneralLedger = lazyWithRetry(() => import("./pages/finance/GeneralLedger"));
const EngagementDashboard = lazyWithRetry(() => import("./pages/EngagementDashboard"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const Organizations = lazyWithRetry(() => import("./pages/Organizations"));
const CommunityMerge = lazyWithRetry(() => import("./pages/CommunityMerge"));
const CommunityDirectory = lazyWithRetry(() => import("./pages/CommunityDirectory"));
const DirectoryRolesPage = lazyWithRetry(() => import("./pages/community/DirectoryRolesPage"));
const DirectoryProfile = lazyWithRetry(() => import("./pages/DirectoryProfile"));
const PersonProfile = lazyWithRetry(() => import("./pages/PersonProfile"));
const HouseholdDetail = lazyWithRetry(() => import("./pages/HouseholdDetail"));
const FamilyGroupDetail = lazyWithRetry(() => import("./pages/FamilyGroupDetail"));
const Contributions = lazyWithRetry(() => import("./pages/Contributions"));
const ContributionImportPage = lazyWithRetry(() => import("./pages/ContributionImportPage"));
const ContributionImportRouteGate = lazyWithRetry(() =>
  import("./components/contributions/ContributionImportFlow").then((module) => ({
    default: module.ContributionImportRouteGate,
  })),
);
const ContributionDetail = lazyWithRetry(() => import("./pages/ContributionDetail"));
const ContributionEdit = lazyWithRetry(() => import("./pages/ContributionEdit"));
const BatchReport = lazyWithRetry(() => import("./pages/BatchReport"));
const BatchGivingEntryPage = lazyWithRetry(() => import("./pages/BatchGivingEntryPage"));
const Pledges = lazyWithRetry(() => import("./pages/Pledges"));
const PledgeDetail = lazyWithRetry(() => import("./pages/PledgeDetail"));
const PledgeSettings = lazyWithRetry(() => import("./pages/PledgeSettings"));
const Funds = lazyWithRetry(() => import("./pages/Funds"));
const FundDetail = lazyWithRetry(() => import("./pages/FundDetail"));
import { ReportsAccessGate } from "@/components/reports/ReportsAccessGate";
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const ReportRunner = lazyWithRetry(() => import("./pages/reports/ReportRunner"));
const ReportPacks = lazyWithRetry(() => import("./pages/reports/ReportPacks"));
const ReportPackEditor = lazyWithRetry(() => import("./pages/reports/ReportPackEditor"));
const MonthlyBoardSummary = lazyWithRetry(() => import("./pages/reports/MonthlyBoardSummary"));
const BoardPack = lazyWithRetry(() => import("./pages/reports/BoardPack"));
const StatementReaderPage = lazyWithRetry(() => import("./pages/reports/StatementReaderPage"));
const ReportPrintView = lazyWithRetry(() => import("./pages/reports/ReportPrintView"));
const RequestsRouter = lazyWithRetry(() => import("./pages/RequestsRouter"));
const Automations = lazyWithRetry(() => import("./pages/Automations"));
const Projects = lazyWithRetry(() => import("./pages/Projects"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const EventDetail = lazyWithRetry(() => import("./pages/EventDetail"));
// CalendarEventDetail removed — /calendar/:eventId now redirects to /events/:eventId
const Attendance = lazyWithRetry(() => import("./pages/Attendance"));
const KidsCheckIn = lazyWithRetry(() => import("./pages/KidsCheckIn"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const ServiceDetail = lazyWithRetry(() => import("./pages/ServiceDetail"));
const SongLibrary = lazyWithRetry(() => import("./pages/SongLibrary"));
const ServiceLive = lazyWithRetry(() => import("./pages/ServiceLive"));
const Sermons = lazyWithRetry(() => import("./pages/Sermons"));
const Volunteers = lazyWithRetry(() => import("./pages/Volunteers"));
const ServingTeams = lazyWithRetry(() => import("./pages/ServingTeams"));
const ServingTeamDetail = lazyWithRetry(() => import("./pages/ServingTeamDetail"));
const MinistryDetail = lazyWithRetry(() => import("./pages/MinistryDetail"));
const MinistryHub = lazyWithRetry(() => import("./pages/MinistryHub"));
const VolunteerDashboard = lazyWithRetry(() => import("./pages/VolunteerDashboard"));
const StaffToday = lazyWithRetry(() => import("./pages/StaffToday"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const RequestCreateRedirect = lazyWithRetry(() => import("./pages/RequestCreateRedirect"));
const RequestRedirect = lazyWithRetry(() => import("./pages/RequestRedirect"));
const Integrations = lazyWithRetry(() => import("./pages/Integrations"));
const StripeIntegration = lazyWithRetry(() => import("./pages/StripeIntegration"));
const IntegrationCallback = lazyWithRetry(() => import("./pages/IntegrationCallback"));
const StripeConnectCallback = lazyWithRetry(() => import("./pages/StripeConnectCallback"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Forbidden = lazyWithRetry(() => import("./pages/Forbidden"));
const AccountDisabled = lazyWithRetry(() => import("./pages/AccountDisabled"));
const AcceptInvite = lazyWithRetry(() => import("./pages/AcceptInvite"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const RecoverAccount = lazyWithRetry(() => import("./pages/RecoverAccount"));
const MfaSetup = lazyWithRetry(() => import("./pages/MfaSetup"));
const MfaChallenge = lazyWithRetry(() => import("./pages/MfaChallenge"));
const Assets = lazyWithRetry(() => import("./pages/Assets"));
const RoomBookings = lazyWithRetry(() => import("./pages/RoomBookings"));
const Messaging = lazyWithRetry(() => import("./pages/Messaging"));
const SentMessages = lazyWithRetry(() => import("./pages/messaging/SentMessages"));
const MessagingHealth = lazyWithRetry(() => import("./pages/messaging/MessagingHealth"));
const CommunicationTemplates = lazyWithRetry(() => import("./pages/CommunicationTemplates"));
const Articles = lazyWithRetry(() => import("./features/articles/ArticlesPage"));
const DirectMessages = lazyWithRetry(() => import("./pages/DirectMessages"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const Announcements = lazyWithRetry(() => import("./pages/Announcements"));
const AnnouncementDetail = lazyWithRetry(() => import("./pages/AnnouncementDetail"));
const AnnouncementNew = lazyWithRetry(() => import("./pages/AnnouncementNew"));
const PublicPrayerRequest = lazyWithRetry(() => import("./pages/PublicPrayerRequest"));

// Bulletins
const BulletinsList = lazyWithRetry(() => import("./pages/bulletins/BulletinsList"));
const BulletinBuilderPage = lazyWithRetry(() => import("./pages/bulletins/BulletinBuilderPage"));
const BulletinDetailView = lazyWithRetry(() => import("./pages/bulletins/BulletinDetailView"));
const BulletinPrintView = lazyWithRetry(() => import("./pages/bulletins/BulletinPrintView"));
const BulletinDuplicateInsert = lazyWithRetry(() => import("./pages/bulletins/BulletinDuplicateInsert"));
const PublicBulletinView = lazyWithRetry(() => import("./pages/bulletins/PublicBulletinView"));

// Public pages
const PublicGivingPage = lazyWithRetry(() => import("./pages/public/PublicGivingPage"));
const PublicGroupsDirectory = lazyWithRetry(() => import("./pages/public/PublicGroupsDirectory"));
const PublicJoinPage = lazyWithRetry(() => import("./pages/public/PublicJoinPage"));
const PublicFormPage = lazyWithRetry(() => import("./pages/public/PublicFormPage"));
const PublicDocumentView = lazyWithRetry(() => import("./pages/public/PublicDocumentView"));

// Relay CRM lives at apps/relay/ (standalone SPA at relay.gettheinspirededge.com).
// Do NOT add Relay pages or routes here.


// Admin pages
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminOrganizations = lazyWithRetry(() => import("./pages/admin/AdminOrganizations"));
const AdminOrganizationDetail = lazyWithRetry(() => import("./pages/admin/AdminOrganizationDetail"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazyWithRetry(() => import("./pages/admin/AdminUserDetail"));
const AdminSearchAnalytics = lazyWithRetry(() => import("./pages/admin/AdminSearchAnalytics"));
const AdminAuditLogs = lazyWithRetry(() => import("./pages/admin/AdminAuditLogs"));
const AdminObservability = lazyWithRetry(() => import("./pages/admin/AdminObservability"));
const AdminWorkflowExecutions = lazyWithRetry(() => import("./pages/admin/AdminWorkflowExecutions"));
const AdminBilling = lazyWithRetry(() => import("./pages/admin/AdminBilling"));
const AdminRequests = lazyWithRetry(() => import("./pages/admin/AdminRequests"));
const AdminRequestDetail = lazyWithRetry(() => import("./pages/admin/AdminRequestDetail"));
const AdminSupportSessions = lazyWithRetry(() => import("./pages/admin/AdminSupportSessions"));
const AdminTelemetry = lazyWithRetry(() => import("./pages/admin/AdminTelemetry"));
const AdminZohoIntegration = lazyWithRetry(() => import("./pages/admin/AdminZohoIntegration"));
const AdminCommunications = lazyWithRetry(() => import("./pages/admin/AdminCommunications"));
const AdminFeatureFlags = lazyWithRetry(() => import("./pages/admin/AdminFeatureFlags"));
const AdminAIModelSettings = lazyWithRetry(() => import("./pages/admin/AdminAIModelSettings"));
const JobRunner = lazyWithRetry(() => import("./pages/admin/JobRunner"));

// Member portal pages
const MemberSignIn = lazyWithRetry(() => import("./pages/member/MemberSignIn"));
const MemberOverview = lazyWithRetry(() => import("./pages/member/MemberOverview"));
const MemberGivingStatements = lazyWithRetry(() => import("./pages/member/MemberGivingStatements"));
const MemberGroups = lazyWithRetry(() => import("./pages/member/MemberGroups"));
const MemberGroupDetail = lazyWithRetry(() => import("./pages/member/MemberGroupDetail"));
const MemberEvents = lazyWithRetry(() => import("./pages/member/MemberEvents"));
const MemberEventDetail = lazyWithRetry(() => import("./pages/member/MemberEventDetail"));
const MemberRegistrations = lazyWithRetry(() => import("./pages/member/MemberRegistrations"));
const MemberAttendance = lazyWithRetry(() => import("./pages/member/MemberAttendance"));
const MemberPledges = lazyWithRetry(() => import("./pages/member/MemberPledges"));
const MemberRequests = lazyWithRetry(() => import("./pages/member/MemberRequests"));
const MemberRequestNew = lazyWithRetry(() => import("./pages/member/MemberRequestNew"));
const MemberNotifications = lazyWithRetry(() => import("./pages/member/MemberNotifications"));
const MemberAnnouncements = lazyWithRetry(() => import("./pages/member/MemberAnnouncements"));
const MemberFamilyPreferences = lazyWithRetry(() => import("./pages/member/MemberFamilyPreferences"));
const MemberSchoolDailyReports = lazyWithRetry(() => import("./pages/member/MemberSchoolDailyReports"));
const MemberPreferences = lazyWithRetry(() => import("./pages/member/MemberPreferences"));
const MemberSettings = lazyWithRetry(() => import("./pages/member/MemberSettings"));
const MemberProfile = lazyWithRetry(() => import("./pages/member/MemberProfile"));
const MemberHousehold = lazyWithRetry(() => import("./pages/member/MemberHousehold"));
const MemberDirectory = lazyWithRetry(() => import("./pages/member/MemberDirectory"));
const MemberDirectoryProfile = lazyWithRetry(() => import("./pages/member/MemberDirectoryProfile"));
const MemberBulletin = lazyWithRetry(() => import("./pages/member/MemberBulletin"));
const MemberWeeklyBulletin = lazyWithRetry(() => import("./pages/member/MemberWeeklyBulletin"));
const MemberSchedule = lazyWithRetry(() => import("./pages/member/MemberSchedule"));
const MemberAvailability = lazyWithRetry(() => import("./pages/member/MemberAvailability"));
const MemberPrayerWall = lazyWithRetry(() => import("./pages/member/MemberPrayerWall"));
const MemberMediaLibrary = lazyWithRetry(() => import("./pages/member/MemberMediaLibrary"));
const MemberMessages = lazyWithRetry(() => import("./pages/member/MemberMessages"));

// Volunteer pages
const VolunteerLanding = lazyWithRetry(() => import("./pages/volunteer/VolunteerLanding"));
const VolunteerSchedule = lazyWithRetry(() => import("./pages/volunteer/VolunteerSchedule"));
const VolunteerAvailability = lazyWithRetry(() => import("./pages/volunteer/VolunteerAvailability"));
const VolunteerSubRequests = lazyWithRetry(() => import("./pages/volunteer/VolunteerSubRequests"));
const VolunteerTasks = lazyWithRetry(() => import("./pages/volunteer/VolunteerTasks"));
const VolunteerRequests = lazyWithRetry(() => import("./pages/volunteer/VolunteerRequests"));
const VolunteerRequestNew = lazyWithRetry(() => import("./pages/volunteer/VolunteerRequestNew"));
const VolunteerProfile = lazyWithRetry(() => import("./pages/volunteer/VolunteerProfile"));
const VolunteerMessages = lazyWithRetry(() => import("./pages/volunteer/VolunteerMessages"));
const VolunteerLeaderServices = lazyWithRetry(() => import("./pages/volunteer/leader/VolunteerLeaderServices"));
const VolunteerLeaderRoster = lazyWithRetry(() => import("./pages/volunteer/leader/VolunteerLeaderRoster"));
const VolunteerLeaderRequests = lazyWithRetry(() => import("./pages/volunteer/leader/VolunteerLeaderRequests"));
const VolunteerLeaderReports = lazyWithRetry(() => import("./pages/volunteer/leader/VolunteerLeaderReports"));

// HR pages
const HrDashboard = lazyWithRetry(() => import("./pages/HrDashboard"));
const HrDocuments = lazyWithRetry(() => import("./pages/HrDocuments"));
const HrOffboarding = lazyWithRetry(() => import("./pages/HrOffboarding"));
const HrOnboarding = lazyWithRetry(() => import("./pages/HrOnboarding"));
const HrPositions = lazyWithRetry(() => import("./pages/HrPositions"));
const HrRecruitment = lazyWithRetry(() => import("./pages/HrRecruitment"));
const HrRequests = lazyWithRetry(() => import("./pages/HrRequests"));
const HrReviews = lazyWithRetry(() => import("./pages/HrReviews"));
const HrSettings = lazyWithRetry(() => import("./pages/HrSettings"));
const HrStaff = lazyWithRetry(() => import("./pages/HrStaff"));
const HrStaffProfile = lazyWithRetry(() => import("./pages/HrStaffProfile"));
const HrTimeOff = lazyWithRetry(() => import("./pages/HrTimeOff"));

// People module
const VisitorFollowUp = lazyWithRetry(() => import("./pages/people/VisitorFollowUp"));
const LifeEvents = lazyWithRetry(() => import("./pages/people/LifeEvents"));
const EngagementScoring = lazyWithRetry(() => import("./pages/people/EngagementScoring"));
const MembershipPathways = lazyWithRetry(() => import("./pages/people/MembershipPathways"));

// Volunteer module
const BackgroundChecks = lazyWithRetry(() => import("./pages/volunteer/BackgroundChecks"));
const Certifications = lazyWithRetry(() => import("./pages/volunteer/Certifications"));
const VolunteerOnboarding = lazyWithRetry(() => import("./pages/volunteer/VolunteerOnboarding"));
const VolunteerHours = lazyWithRetry(() => import("./pages/volunteer/VolunteerHours"));

// Groups module
const GroupFinder = lazyWithRetry(() => import("./pages/groups/GroupFinder"));
const GroupManagement = lazyWithRetry(() => import("./pages/groups/GroupManagement"));
const CurriculumManagement = lazyWithRetry(() => import("./pages/groups/CurriculumManagement"));
const GroupHealthDashboard = lazyWithRetry(() => import("./pages/groups/GroupHealthDashboard"));
const GroupCommunication = lazyWithRetry(() => import("./pages/groups/GroupCommunication"));

// Finance module additions
const MyGivingRedirect = lazyWithRetry(() => import("./pages/MyGivingRedirect"));
const PledgeReminders = lazyWithRetry(() => import("./pages/finance/PledgeReminders"));
const RecurringGiving = lazyWithRetry(() => import("./pages/finance/RecurringGiving"));
const ApInboundEmail = lazyWithRetry(() => import("./pages/finance/ApInboundEmail"));
const DonorAnalytics = lazyWithRetry(() => import("./pages/finance/DonorAnalytics"));
const CampaignDashboard = lazyWithRetry(() => import("./pages/finance/CampaignDashboard"));
const FundComplianceQueue = lazyWithRetry(() => import("./pages/finance/FundComplianceQueue"));

// Schools module
const SchoolsHomePage = lazyWithRetry(() => import("./pages/schools/SchoolsHomePage"));
const SchoolsAcademicsPage = lazyWithRetry(() => import("./pages/schools/SchoolsAcademicsPage"));
const SchoolsGradebookPage = lazyWithRetry(() => import("./pages/schools/SchoolsGradebookPage"));
const SchoolsReportCardsPage = lazyWithRetry(() => import("./pages/schools/SchoolsReportCardsPage"));
const SchoolsReportExportsPage = lazyWithRetry(() => import("./pages/schools/SchoolsReportExportsPage"));
const SchoolsTranscriptsPage = lazyWithRetry(() => import("./pages/schools/SchoolsTranscriptsPage"));
const SchoolsSacramentsPage = lazyWithRetry(() => import("./pages/schools/SchoolsSacramentsPage"));
const SchoolsServiceHoursPage = lazyWithRetry(() => import("./pages/schools/SchoolsServiceHoursPage"));
const SchoolsAdmissionsPage = lazyWithRetry(() => import("./pages/schools/SchoolsAdmissionsPage"));
const SchoolsTuitionPage = lazyWithRetry(() => import("./pages/schools/SchoolsTuitionPage"));
const SchoolsBillingPage = lazyWithRetry(() => import("./pages/schools/SchoolsBillingPage"));
const SchoolsDiscountsAidPage = lazyWithRetry(() => import("./pages/schools/SchoolsDiscountsAidPage"));
const SchoolsEnrollmentsPage = lazyWithRetry(() => import("./pages/schools/SchoolsEnrollmentsPage"));
const SchoolsContractsPage = lazyWithRetry(() => import("./pages/schools/SchoolsContractsPage"));
const SchoolsAutopayPage = lazyWithRetry(() => import("./pages/schools/SchoolsAutopayPage"));
const SchoolsRosterPage = lazyWithRetry(() => import("./pages/schools/SchoolsRosterPage"));
const SchoolsDailyReportsPage = lazyWithRetry(() => import("./pages/schools/SchoolsDailyReportsPage"));
const SchoolsHealthPage = lazyWithRetry(() => import("./pages/schools/SchoolsHealthPage"));
const SchoolsAttendancePage = lazyWithRetry(() => import("./pages/schools/SchoolsAttendancePage"));
const SchoolsChapelAttendancePage = lazyWithRetry(() => import("./pages/schools/SchoolsChapelAttendancePage"));

// Communications module
const DripCampaigns = lazyWithRetry(() => import("./pages/communications/DripCampaigns"));
const BroadcastCalendar = lazyWithRetry(() => import("./pages/communications/BroadcastCalendar"));
const ABTesting = lazyWithRetry(() => import("./pages/communications/ABTesting"));
const SmsUsageDashboard = lazyWithRetry(() => import("./pages/communications/SmsUsageDashboard"));

// Events module additions
const EventTemplates = lazyWithRetry(() => import("./pages/events/EventTemplates"));
const WaitlistManager = lazyWithRetry(() => import("./pages/events/WaitlistManager"));
const PublicEventsPage = lazyWithRetry(() => import("./pages/events/PublicEventsPage"));
const GeneralCheckIn = lazyWithRetry(() => import("./pages/events/GeneralCheckIn"));
const CheckInKiosk = lazyWithRetry(() => import("./pages/events/CheckInKiosk"));

// Self-registration (public, no auth)
const SelfRegistration = lazyWithRetry(() => import("./pages/SelfRegistration"));
const RegistrationApprovals = lazyWithRetry(() => import("./pages/RegistrationApprovals"));
const Forms = lazyWithRetry(() => import("./pages/Forms"));
const Documents = lazyWithRetry(() => import("./pages/Documents"));

// HR module additions
const CompensationHistory = lazyWithRetry(() => import("./pages/hr/CompensationHistory"));
const BenefitsSummary = lazyWithRetry(() => import("./pages/hr/BenefitsSummary"));
const OrgChart = lazyWithRetry(() => import("./pages/hr/OrgChart"));

// Member portal additions
const SermonArchive = lazyWithRetry(() => import("./pages/member/SermonArchive"));

// Pastoral Care module
const PastoralCare = lazyWithRetry(() => import("./pages/PastoralCare"));
const PastoralCareDetail = lazyWithRetry(() => import("./pages/PastoralCareDetail"));

// A change of signed-in user loads the page afresh, ending anything the previous user left in
// flight (#7026). See src/lib/reload-on-user-change.ts.
reloadOnSignedInUserChange();

const LegacyDirectoryProfileRedirect = () => {
  const { personId } = useParams();
  return <Navigate to={personId ? `/community/directory/${personId}` : "/community/directory"} replace />;
};

// Preserves the query string (e.g. /community?create=true from Staff Today's
// "Add person" quick action, which Community.tsx reads to auto-open the add
// dialog) — a bare `<Navigate to="/people/all" replace />` would drop it.
const LegacyPeopleRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: PEOPLE_ALL_PATH, search: location.search }} replace />;
};

// T5 trail for People's directory settings. Carries ?ui= so a preview user whose storage is
// blocked keeps the new layout through the crumbs.
const PeopleDirectorySettingsPage = () => {
  const ui = new URLSearchParams(useLocation().search).get("ui");
  const withUi = (href: string) => (ui === "next" || ui === "classic" ? `${href}?ui=${ui}` : href);
  return (
    <DirectoryAdminSettings
      embedded
      breadcrumbs={[
        // Straight to the staff home: "/" redirects to it and drops the query string.
        { label: "Home", href: withUi("/today") },
        { label: "People", href: withUi("/people") },
        { label: "Settings", href: withUi(PEOPLE_SETTINGS_PATH) },
        { label: "Directory settings" },
      ]}
    />
  );
};

const NavigateCalendarEvent = () => {
  const { eventId } = useParams();
  return <Navigate to={eventId ? `/events/${eventId}` : "/events"} replace />;
};

// DARK_MODE.md: the check-in kiosk is always light. Enforced from here, above <Routes>, so
// the parent access guard's loading and error fallbacks (rendered before the kiosk route
// mounts) are light too, and the toaster follows it (the class alone leaves the
// ThemeProvider context on "dark", which Sonner reads).
function useIsKioskRoute(): boolean {
  const { pathname } = useLocation();
  return isKioskPathname(pathname);
}

function KioskLightTheme() {
  const kiosk = useIsKioskRoute();
  const { pathname } = useLocation();
  useForcedLightTheme(kiosk);
  // DECISIONS.md D1: station buttons and fields are touch-sized at any width (the
  // `[data-surface="kiosk"]` rule in src/styles/tokens.css). That is all three check-in stations;
  // only the kiosk is also forced light (#8032).
  useTouchSurface(isTouchStationPathname(pathname));
  return null;
}

function RouteAwareToaster() {
  // An explicit `theme={undefined}` would override the ThemeProvider value inside Sonner,
  // so only pass the prop on the kiosk and on public pages the church forced light.
  const kiosk = useIsKioskRoute();
  const publicForcedLight = usePublicForceLightActive();
  // `theme` alone only reaches Sonner's own data attribute; the wrapper styles toasts from the
  // app's CSS tokens and `dark:` classes, so the toaster also needs the `.force-light` token
  // scope (it keeps the Tailwind `dark:` variant off for itself and its descendants).
  return kiosk === true || publicForcedLight === true ? (
    <Sonner theme="light" className="toaster group force-light" />
  ) : (
    <Sonner />
  );
}

// See src/lib/spa-navigation-tracker.ts for why this exists and lives here
// (always mounted, inside the router, alongside RoutePrefetchWatcher) rather
// than as a per-link opt-in.
function SpaNavigationTracker() {
  const location = useLocation();

  useEffect(() => {
    // location.key (not pathname) so a navigation back to the same path
    // still counts as a distinct route render. recordRouteRender dedupes by
    // this same key, which is also what makes it safe against StrictMode's
    // double-invoked mount effect in development (see that module's own
    // comment) -- both invocations report the identical key.
    recordRouteRender(location.key);
  }, [location.key]);

  return null;
}

function RoutePrefetchWatcher() {
  const location = useLocation();
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;
    const runPrefetch = () => preloadRelatedRoutes(location.pathname);

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(runPrefetch, { timeout: 1_500 });
    } else {
      timeoutId = setTimeout(runPrefetch, 800);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (idleId !== null && typeof window !== "undefined" && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [location.pathname]);

  return null;
}

/**
 * Bootstraps Capacitor-specific features when running inside a native shell.
 * Must live inside <BrowserRouter> so hooks like useNavigate are available.
 */
function NativeBootstrap() {
  // Deep link handler (iOS universal links + Android app links)
  useDeepLinks();
  // Native push notification registration
  useNativePush();

  useEffect(() => {
    if (!platform.isNative) return;

    // Notify Capgo that the JS bundle loaded successfully
    CapacitorUpdater.notifyAppReady();

    // Hide the native splash screen once the React shell has rendered
    hideSplash();

    // Listen for available OTA updates
    const updateSub = CapacitorUpdater.addListener('updateAvailable', (update) => {
      console.log('[Capgo] Update available:', update.bundle.version);
    });
    const downloadSub = CapacitorUpdater.addListener('downloadComplete', (download) => {
      console.log('[Capgo] Download complete:', download.bundle.version);
    });

    return () => {
      updateSub.then(l => l.remove());
      downloadSub.then(l => l.remove());
    };
  }, []);

  return null;
}

// Layout with full provider stack for authenticated routes
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  return <AuthenticatedProviders>{children}</AuthenticatedProviders>;
}

// Wrapper component for protected routes with providers
function AuthenticatedRoute({ children, ...props }: { children: React.ReactNode } & React.ComponentProps<typeof ProtectedRoute>) {
  return (
    <AuthenticatedLayout>
      <ProtectedRoute {...props}>{children}</ProtectedRoute>
    </AuthenticatedLayout>
  );
}

// Wrapper for admin routes with providers
function AuthenticatedAdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <AuthenticatedLayout>
      <AdminRoute>{children}</AdminRoute>
    </AuthenticatedLayout>
  );
}

// Wrapper for staff routes with providers and access context gating
function AuthenticatedStaffArea() {
  return (
    <AuthenticatedLayout>
      <ProtectedRoute skipAccessError>
        <StaffAccessGate />
      </ProtectedRoute>
    </AuthenticatedLayout>
  );
}

// Wrapper for routes operable by staff OR a plain volunteer manning a shared
// station (e.g. event check-in). Backend writers/readers re-impose the same
// staff-OR-volunteer authz, so this only governs which UI a volunteer can reach.
function AuthenticatedStaffOrVolunteerArea() {
  return (
    <AuthenticatedLayout>
      <ProtectedRoute skipAccessError>
        <StaffAccessGate allowVolunteers />
      </ProtectedRoute>
    </AuthenticatedLayout>
  );
}

function RoutePolicyErrorBoundary() {
  const { pathname } = useLocation();
  const routePolicy = resolveRoutePolicy(pathname);

  if (isRouteErrorBoundaryExempt(pathname) || routePolicy.errorRecovery === "none") {
    return null;
  }

  return <RouteErrorFallback />;
}

const App = () => (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <RouteAwareToaster />
        <KioskLightTheme />
        <SpaNavigationTracker />
        <PeopleReturnListTracker />
        <RoutePrefetchWatcher />
        <NativeBootstrap />
        {/* Above the routes: each page renders its own AppLayout, so it must outlive them. */}
        <RouteAnnouncer />
        {/* Everything that reads queries sits below this; it remounts with a fresh QueryClient
            whenever the signed-in user changes (#7022). The router, toasts and the native
            bootstrap above it do not read queries and are kept. */}
        <UserScopedQueryClientProvider>
        <GlobalErrorBoundary>
            <AppShell>
            <Suspense fallback={<ForgePageLoader />}>
            <Routes>
              {/* Route-level policy wrapper: applies timeout/error policy to all nested routes.
                Route-level error-boundary exemptions are recorded in
                src/lib/route-policies.ts via policyNote flags. */}
              <Route
                element={
                  <AuthenticatedRouteProviders>
                    <WelcomeFlowProvider>
                      <ChangelogProvider>
                        <RoutePolicyBoundary><Outlet /></RoutePolicyBoundary>
                        <WelcomeFlow />
                        <ChangelogModal />
                      </ChangelogProvider>
                    </WelcomeFlowProvider>
                  </AuthenticatedRouteProviders>
                }
                errorElement={<RoutePolicyErrorBoundary />}
              >
                {/* Public routes - NO providers that use auth hooks */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/member/signin" element={<MemberSignIn />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/recover-account" element={<RecoverAccount />} />
                <Route path="/403" element={<Forbidden />} />
                <Route path="/account-disabled" element={<AccountDisabled />} />
                <Route
                  path="/b/:id"
                  element={
                    <AuthenticatedRoute>
                      <PublicBulletinView />
                    </AuthenticatedRoute>
                  }
                />
                <Route path="/public/:slug/give" element={<PublicGivingPage />} />
                <Route path="/public/:slug/groups" element={<PublicGroupsDirectory />} />
                <Route path="/public/:slug/join" element={<PublicJoinPage />} />
                <Route path="/public/:slug/prayer" element={<PublicPrayerRequest />} />
                <Route path="/public/prayer" element={<PublicPrayerRequest />} />
                <Route path="/public/:slug/events" element={<PublicEventsPage />} />
                <Route path="/f/:orgSlug/:formSlug" element={<PublicFormPage />} />
                <Route path="/d/:token" element={<PublicDocumentView />} />
                <Route path="/register" element={<SelfRegistration />} />

                {/* MFA routes */}
                {FEATURE_FLAGS.mfa ? (
                  <>
                    <Route
                      path="/mfa/setup"
                      element={
                        <AuthenticatedRoute skipOrgCheck skipMfaCheck>
                          <MfaSetup />
                        </AuthenticatedRoute>
                      }
                    />
                    <Route
                      path="/mfa/challenge"
                      element={
                        <AuthenticatedRoute skipOrgCheck skipMfaCheck>
                          <MfaChallenge />
                        </AuthenticatedRoute>
                      }
                    />
                  </>
                ) : (
                  <>
                    <Route path="/mfa/setup" element={<Navigate to="/" replace />} />
                    <Route path="/mfa/challenge" element={<Navigate to="/" replace />} />
                  </>
                )}

                {/* Onboarding */}
                <Route
                  path="/onboarding"
                  element={
                    <AuthenticatedRoute skipOrgCheck>
                      <Onboarding />
                    </AuthenticatedRoute>
                  }
                />

              {/* Main app routes */}
              <Route
                path="/"
                element={
                  <AuthenticatedRoute>
                    <HomeRedirect />
                  </AuthenticatedRoute>
                }
              />

              {/* Member portal routes */}
              <Route
                path="/member"
                element={
                  <AuthenticatedRoute>
                    <MemberShell />
                  </AuthenticatedRoute>
                }
              >
                <Route index element={<MemberOverview />} />
                <Route path="announcements" element={<MemberAnnouncements />} />
                <Route path="profile" element={<MemberProfile />} />
                <Route path="household" element={<MemberHousehold />} />
                <Route path="directory" element={<MemberCapabilityRoute capability="member_directory"><MemberDirectory /></MemberCapabilityRoute>} />
                <Route path="directory/:personId" element={<MemberCapabilityRoute capability="member_directory"><MemberDirectoryProfile /></MemberCapabilityRoute>} />
                {/* Giving history + statements are a baseline portal feature gated by the
                    dedicated `allow_giving_statements` setting (enforced in-page), NOT the
                    `online_giving` payments capability — payments-off churches keep statements. (GIV-5) */}
                <Route path="giving" element={<MemberGivingStatements />} />
                <Route path="give" element={<MyGivingRedirect />} />
                <Route path="groups" element={<MemberCapabilityRoute capability="groups"><MemberGroups /></MemberCapabilityRoute>} />
                <Route path="groups/:groupId" element={<MemberCapabilityRoute capability="groups"><MemberGroupDetail /></MemberCapabilityRoute>} />
                <Route path="messages" element={<MemberMessages />} />
                <Route path="events" element={<MemberEvents />} />
                <Route path="events/:eventId" element={<MemberEventDetail />} />
                <Route path="registrations" element={<MemberRegistrations />} />
                <Route path="attendance" element={<MemberAttendance />} />
                <Route path="pledges" element={<MemberCapabilityRoute capability="pledge_management"><MemberPledges /></MemberCapabilityRoute>} />
                <Route path="requests" element={<MemberRequests />} />
                <Route path="requests/new" element={<MemberRequestNew />} />
                <Route path="prayer" element={<MemberCapabilityRoute capability="prayer_wall"><MemberPrayerWall /></MemberCapabilityRoute>} />
                <Route path="media" element={<MemberMediaLibrary />} />
                <Route path="notifications" element={<MemberNotifications />} />
                <Route path="preferences" element={<MemberPreferences />} />
                <Route path="settings" element={<MemberSettings />} />
                <Route path="bulletin" element={<MemberWeeklyBulletin />} />
                <Route path="bulletins/:week_of" element={<MemberBulletin />} />
                <Route path="schedule" element={<MemberSchedule />} />
                <Route path="availability" element={<MemberAvailability />} />
                <Route path="family/preferences" element={<MemberCapabilityRoute capability="kids_checkin"><MemberFamilyPreferences /></MemberCapabilityRoute>} />
                <Route path="family/daily-reports" element={<MemberCapabilityRoute capability="kids_checkin"><MemberSchoolDailyReports /></MemberCapabilityRoute>} />
              </Route>

              <Route
                path="/portal"
                element={
                  <AuthenticatedRoute>
                    <MemberShell />
                  </AuthenticatedRoute>
                }
              >
                <Route index element={<MemberOverview />} />
                <Route path="announcements" element={<MemberAnnouncements />} />
                {/* Legacy mount: the canonical, nav-linked route is /member/family/preferences. */}
                <Route path="family/preferences" element={<Navigate to="/member/family/preferences" replace />} />
              </Route>

              {/* My Portal routes */}
              {/* Legacy /my/giving (MemberGiving) recorded an uncharged "pending" gift and
                  showed a false success — retired. Redirect to the real (public) give surface. (GIV-1) */}
              <Route
                path="/my/giving"
                element={
                  <AuthenticatedRoute>
                    <Suspense fallback={<ForgePageLoader />}>
                      <MyGivingRedirect />
                    </Suspense>
                  </AuthenticatedRoute>
                }
              />
              <Route
                path="/my/sermons"
                element={
                  <AuthenticatedRoute>
                    <Suspense fallback={<ForgePageLoader />}>
                      <SermonArchive />
                    </Suspense>
                  </AuthenticatedRoute>
                }
              />

              {/* Volunteer routes */}
              <Route
                path="/volunteer"
                element={
                  <AuthenticatedRoute>
                    <VolunteerShell />
                  </AuthenticatedRoute>
                }
              >
                <Route index element={<VolunteerLanding />} />
                <Route path="schedule" element={<VolunteerSchedule />} />
                <Route path="availability" element={<VolunteerAvailability />} />
                <Route path="sub-requests" element={<VolunteerSubRequests />} />
                <Route path="tasks" element={<VolunteerTasks />} />
                <Route path="requests" element={<VolunteerRequests />} />
                <Route path="requests/new" element={<VolunteerRequestNew />} />
                <Route path="profile" element={<VolunteerProfile />} />
                <Route path="messages" element={<VolunteerMessages />} />
                <Route path="leader/services" element={<VolunteerLeaderServices />} />
                <Route path="leader/roster" element={<VolunteerLeaderRoster />} />
                <Route path="leader/requests" element={<VolunteerLeaderRequests />} />
                <Route path="leader/reports" element={<VolunteerLeaderReports />} />
              </Route>

              {/* Staff-or-volunteer routes — shared check-in stations a volunteer can operate */}
              <Route element={<AuthenticatedStaffOrVolunteerArea />}>
                <Route
                  path={GENERAL_CHECK_IN_PATH}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<ForgePageLoader />}>
                        <GeneralCheckIn />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={KIOSK_PATH}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<ForgePageLoader />}>
                        <CheckInKiosk />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                {/* O6: /services + /services/:serviceId are READ surfaces. The
                    services SELECT RLS admits org members (calendar-visibility
                    scoped) and the volunteer matrix grants services:{view}, so a
                    plain volunteer reaches them read-only. Create/edit affordances
                    are gated inline (Services "New Service" on can('services','add');
                    ServiceDetailContent write actions on can('services','edit')).
                    /services/:serviceId/live and service writes stay staff-only. */}
                <Route
                  path="/services"
                  element={<Suspense fallback={<ForgePageLoader />}><Services /></Suspense>}
                />
                <Route
                  path="/services/:serviceId"
                  element={<Suspense fallback={<ForgePageLoader />}><ServiceDetail /></Suspense>}
                />
              </Route>

              {/* Staff routes */}
              <Route element={<AuthenticatedRoute skipAccessError><TaskNotificationGate /></AuthenticatedRoute>}>
                <Route element={<StaffAccessGate />}>
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/tasks/:taskId" element={<Tasks />} />
                </Route>
              </Route>
              <Route element={<AuthenticatedStaffArea />}>
            <Route
              path="/dashboard"
              element={<Index />}
            />
            <Route
              path="/forms"
              element={<Forms />}
            />
            <Route
              path="/documents"
              element={<Documents />}
            />
            {/* Relay CRM is a standalone app at apps/relay/ — no routes here */}
            <Route
              path="/finance"
              element={<Navigate to="/finance/dashboard" replace />}
            />
            <Route
              path="/engagement"
              element={<TierRouteGate module="engagement_dashboards"><EngagementDashboard /></TierRouteGate>}
            />
            {/* /people becomes the Overview (T3, #6698); until then it lands on Everyone. */}
            <Route path="/people" element={<LegacyPathRedirect to={PEOPLE_ALL_PATH} />} />
            <Route
              path={PEOPLE_ALL_PATH}
              element={<Community />}
            />
            <Route
              path="/community/organizations"
              element={<Organizations />}
            />
            <Route
              path="/community/merge"
              element={<CommunityMerge />}
            />
            <Route
              path="/community/registration-approvals"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <RegistrationApprovals />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/directory"
              element={<Navigate to="/community/directory" replace />}
            />
            <Route
              path="/directory/:personId"
              element={<LegacyDirectoryProfileRedirect />}
            />
            <Route
              path="/community/directory"
              element={<CommunityDirectory />}
            />
            <Route path="/community/directory-roles" element={<DirectoryRolesPage />} />
            {/* People settings (T5, #6679). Directory settings stay reachable at /settings/directory
                too until the Settings area's own link moves. Directory roles keeps its own route: its
                permission model (announcement editors) differs from this org-admin page. */}
            <Route
              path={PEOPLE_SETTINGS_PATH}
              element={
                <OrgAdminRoute>
                  <AppLayout>
                    <CommunityLayout>
                      <PeopleDirectorySettingsPage />
                    </CommunityLayout>
                  </AppLayout>
                </OrgAdminRoute>
              }
            />
            <Route
              path="/community/directory/:personId"
              element={<DirectoryProfile />}
            />
            {/* L8: gate the whole /hr* subtree on the HR module perm so personas
                with hr:{} (youth, ministry_leader, bookkeeper, ap/ar/approver,
                volunteer, member) can't reach HR by direct URL. The HR nav link is
                already hidden for them (AppSidebar canAccessModule('hr')); this
                hardens the route to match. Inner TierRouteGate / StaffRoute /
                CapabilityRouteGate guards remain as defense-in-depth. */}
            <Route element={<ModuleRouteGate module="hr"><Outlet /></ModuleRouteGate>}>
            <Route
              path="/hr"
              element={
                <TierRouteGate module="hr_staff">
                  <Suspense fallback={<ForgePageLoader />}>
                    <HrDashboard />
                  </Suspense>
                </TierRouteGate>
              }
            />
            <Route
              path="/hr/staff"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrStaff />
                </Suspense>
              }
            />
            <Route
              path="/hr/staff/:staffId"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrStaffProfile />
                </Suspense>
              }
            />
            {/* The T2 profile's tabs are routes (#6865); classic ignores the segment. */}
            <Route
              path="/hr/staff/:staffId/:tab"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrStaffProfile />
                </Suspense>
              }
            />
            <Route
              path="/hr/positions"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrPositions />
                </Suspense>
              }
            />
            <Route
              path="/hr/recruitment"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrRecruitment />
                </Suspense>
              }
            />
            <Route
              path="/hr/reviews"
              element={
                <CapabilityRouteGate capability="hr_staff.performance_reviews" toggledOffRedirect="/hr">
                  <Suspense fallback={<ForgePageLoader />}>
                    <HrReviews />
                  </Suspense>
                </CapabilityRouteGate>
              }
            />
            <Route
              path="/hr/onboarding"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrOnboarding />
                </Suspense>
              }
            />
            <Route
              path="/hr/time-off"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrTimeOff />
                </Suspense>
              }
            />
            <Route
              path="/hr/documents"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrDocuments />
                </Suspense>
              }
            />
            <Route
              path="/hr/requests"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrRequests />
                </Suspense>
              }
            />
            <Route
              path="/hr/offboarding"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrOffboarding />
                </Suspense>
              }
            />
            <Route
              path="/hr/settings"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <HrSettings />
                </Suspense>
              }
            />
            <Route
              path="/hr/compensation"
              element={
                <StaffRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <CompensationHistory />
                  </Suspense>
                </StaffRoute>
              }
            />
            <Route
              path="/hr/benefits"
              element={
                <StaffRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <BenefitsSummary />
                  </Suspense>
                </StaffRoute>
              }
            />
            <Route
              path="/hr/org-chart"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <OrgChart />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            </Route>
            {/* Redirect old routes */}
            <Route path="/community" element={<LegacyPeopleRedirect />} />
            <Route path="/households" element={<LegacyPeopleRedirect />} />
            <Route path="/people/households" element={<LegacyPeopleRedirect />} />
            <Route path="/people/visitors" element={<Navigate to="/people/visitor-followup" replace />} />
            <Route path="/people/engagement" element={<LegacyPathRedirect to={PEOPLE_ENGAGEMENT_SETTINGS_PATH} />} />
            <Route path="/people/engagement-scoring" element={<LegacyPathRedirect to={PEOPLE_ENGAGEMENT_SETTINGS_PATH} />} />
            <Route path="/member-portal" element={<Navigate to="/member" replace />} />
            <Route path="/ministry/services" element={<Navigate to="/services" replace />} />
            <Route path="/operations" element={<Navigate to="/dashboard" replace />} />
            {/* Finance legacy/alternate routes */}
            <Route path="/finance/fiscal-periods" element={<Navigate to="/finance/month-end-close" replace />} />
            <Route path="/finance/ap/vendors" element={<Navigate to="/finance/vendor-1099" replace />} />
            <Route path="/finance/vendors" element={<Navigate to="/finance/vendor-1099" replace />} />
            <Route path="/finance/pledges" element={<Navigate to="/finance/pledge-reminders" replace />} />
            {/* Groups legacy routes */}
            <Route path="/groups/messages" element={<Navigate to="/groups/communication" replace />} />
            <Route
              path="/people/visitor-followup"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <VisitorFollowUp />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/life-events"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <LifeEvents />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path={PEOPLE_ENGAGEMENT_SETTINGS_PATH}
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <EngagementScoring />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/membership-pathways"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <MembershipPathways />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/:personId/:tab?"
              element={<PersonProfile />}
            />
            <Route
              path="/households/:householdId"
              element={<HouseholdDetail />}
            />
            <Route
              path="/family-groups/:familyGroupId"
              element={<FamilyGroupDetail />}
            />
            <Route
              path="/contributions"
              element={<ContributionImportRedirect><Contributions /></ContributionImportRedirect>}
            />
            <Route
              path="/contributions/import"
              element={
                <Suspense fallback={<ForgePageLoader />}>
                  <ContributionImportRouteGate><ContributionImportPage /></ContributionImportRouteGate>
                </Suspense>
              }
            />
            <Route
              path="/contributions/new"
              element={<Contributions />}
            />
            {/* Sprint 3G: Legacy URL redirect */}
            <Route
              path="/giving"
              element={<Navigate to="/contributions" replace />}
            />
            <Route
              path="/contributions/:contributionId"
              element={<ContributionDetail />}
            />
            <Route
              path="/contributions/:contributionId/edit"
              element={<ContributionEdit />}
            />
            {/* The next layout's route tabs (/contributions/:id/activity); classic redirects to the record. */}
            <Route
              path="/contributions/:contributionId/:tab"
              element={<ContributionDetail />}
            />
            <Route
              path="/batch-report"
              element={<BatchReport />}
            />
            <Route
              path="/finance/giving/batches/new"
              element={<BatchGivingEntryPage />}
            />
            <Route
              path="/pledges"
              element={<TierRouteGate module="pledge_management"><Pledges /></TierRouteGate>}
            />
            {/* Gated like /pledges, not like Finance settings: an organization with pledges and without accounting has to
                be able to change who can cancel one (#7097). A static segment outranks :pledgeId. */}
            <Route
              path="/pledges/settings"
              element={<TierRouteGate module="pledge_management"><PledgeSettings /></TierRouteGate>}
            />
            <Route
              path="/pledges/:pledgeId/:section?"
              element={<TierRouteGate module="pledge_management"><PledgeDetail /></TierRouteGate>}
            />
            <Route
              path="/funds"
              element={<Funds />}
            />
            <Route
              path="/funds/:fundId/:tab?"
              element={<FundDetail />}
            />
            <Route
              path="/finance/integration"
              element={(
                <QboFinanceRouteGate>
                  <FinanceIntegration />
                </QboFinanceRouteGate>
              )}
            />
            <Route
              path="/finance/integration/callback"
              element={(
                <OrgAdminRoute>
                  <IntegrationCallback />
                </OrgAdminRoute>
              )}
            />
            <Route
              path="/finance/integration/settings"
              element={(
                <QboFinanceRouteGate>
                  <IntegrationSettings />
                </QboFinanceRouteGate>
              )}
            />
            <Route
              path="/finance/audit"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceAuditLog />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/queue"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceQueue />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/banking"
              element={(
                <ModuleRouteGate module="contributions">
                  <BankingHome />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/deposits"
              element={(
                <ModuleRouteGate module="contributions">
                  <Deposits />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/deposits/:depositId/:section?"
              element={(
                <ModuleRouteGate module="contributions">
                  <DepositDetail />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/posting-log"
              element={(
                <ModuleRouteGate module="contributions">
                  <PostingLog />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/journals"
              element={(
                <ModuleRouteGate module="contributions" fallbackModules={["reports"]}>
                  <JournalViewer />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/journals/new"
              element={(
                <ModuleRouteGate module="contributions">
                  <ManualJournalEntry />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/journals/recurring"
              element={(
                <ModuleRouteGate module="contributions">
                  <RecurringJournals />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/audit-trail"
              element={(
                <ModuleRouteGate module="contributions" fallbackModules={["reports"]}>
                  <AuditTrail />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/chart-of-accounts"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <ChartOfAccounts />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/general-ledger"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <GeneralLedger />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/recurring-expenses"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <RecurringExpenses />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/ap"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <AccountsPayable />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            {/* Scan-to-email intake moved into Finance settings (T5, #6727); old links land there. */}
            <Route path="/finance/ap/scan-to-email" element={<Navigate to="/finance/settings/ap-inbound-email" replace />} />
            <Route path="/finance/ap/scan" element={<Navigate to="/finance/settings/ap-inbound-email" replace />} />
            <Route
              path="/finance/ar"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <AccountsReceivable />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/budgets"
              element={(
                <ModuleRouteGate module="funds">
                  <Budgets />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/accounting/restricted-releases"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <RestrictedReleases />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/accounting/release-wizard"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <ReleaseWizard />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/month-end-close"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <MonthEndClose />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/settings"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsIndex />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/accounting-setup"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <FinanceSettingsLayout>
                      <AccountingSetup />
                    </FinanceSettingsLayout>
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/settings/contribution-defaults"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <FinanceSettingsLayout>
                      <ContributionDefaultsSettings />
                    </FinanceSettingsLayout>
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/settings/approval-policies"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <ApprovalPolicies />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/payables-settings"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <PayablesSettings />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/ap-inbound-email"
              element={(
                <TierRouteGate module="accounting">
                  <ModuleRouteGate module="contributions">
                    <FinanceSettingsLayout>
                      <ApInboundEmail />
                    </FinanceSettingsLayout>
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/settings/recurring-rules"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <RecurringRulesSettings />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/bank-connections"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <BankConnectionsSettings />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/integrations"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <IntegrationsSettings />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/pre-go-live-cleanup"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <PreGoLiveCleanup />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/settings/allocations"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceSettingsLayout>
                    <AllocationRules />
                  </FinanceSettingsLayout>
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/fund-transfers"
              element={(
                <ModuleRouteGate module="contributions">
                  <FundTransfers />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/depreciation"
              element={(
                <ModuleRouteGate module="contributions">
                  <DepreciationSchedule />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/purchase-orders"
              element={(
                <ModuleRouteGate module="contributions">
                  <PurchaseOrders />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/payroll"
              element={(
                <ModuleRouteGate module="contributions">
                  <PayrollIntegration />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/dashboard"
              element={(
                <ModuleRouteGate module="contributions">
                  <FinanceDashboardPage />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/vendor-1099"
              element={(
                <ModuleRouteGate module="contributions">
                  <Vendor1099 />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/form-990-export"
              element={(
                <ModuleRouteGate module="contributions">
                  <Form990Export />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/housing-allowance"
              element={(
                <ModuleRouteGate module="contributions">
                  <HousingAllowance />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/pledge-reminders"
              element={(
                <TierRouteGate module="pledge_management">
                  <ModuleRouteGate module="contributions">
                    <PledgeReminders />
                  </ModuleRouteGate>
                </TierRouteGate>
              )}
            />
            <Route
              path="/finance/recurring-giving"
              element={(
                <ModuleRouteGate module="contributions">
                  <RecurringGiving />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/donor-analytics"
              element={(
                <ModuleRouteGate module="contributions">
                  <DonorAnalytics />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/campaigns"
              element={(
                <ModuleRouteGate module="contributions">
                  <CampaignDashboard />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/compliance-queue"
              element={(
                <ModuleRouteGate module="funds">
                  <FundComplianceQueue />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/endowment-management"
              element={(
                <ModuleRouteGate module="contributions">
                  <EndowmentManagement />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/conditional-contributions"
              element={(
                <ModuleRouteGate module="contributions">
                  <ConditionalContributions />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/inkind-contributions"
              element={(
                <ModuleRouteGate module="contributions">
                  <InKindContributions />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/pledge-discounting"
              element={(
                <ModuleRouteGate module="contributions">
                  <PledgeDiscounting />
                </ModuleRouteGate>
              )}
            />
            <Route path="/finance/close" element={<Navigate to="/finance/month-end-close" replace />} />
            <Route path="/finance/accounting" element={<Navigate to="/finance/accounting/restricted-releases" replace />} />
            <Route
              path="/finance/reports"
              element={(
                <ModuleRouteGate module="reports">
                  <Navigate to="/reports" replace />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/finance/requests"
              element={(
                <ModuleRouteGate module="requests">
                  <RequestsRouter defaultLane="finance" />
                </ModuleRouteGate>
              )}
            />
            <Route
              path="/automations"
              element={
                <OrgAdminRoute>
                  <Automations />
                </OrgAdminRoute>
              }
            />
            {/* RPT-1: gate the org-wide report surfaces so scope-restricted non-admins
                never mount their org-wide data hooks (route-level, covers runner + print). */}
            <Route element={<ReportsAccessGate />}>
              <Route
                path="/reports"
                element={<Reports />}
              />
              <Route
                path="/reports/packs"
                element={<ReportPacks />}
              />
              <Route
                path="/reports/packs/new"
                element={<ReportPackEditor />}
              />
              <Route
                path="/reports/packs/:packId"
                element={<ReportPackEditor />}
              />
              <Route
                path="/reports/monthly-summary"
                element={<MonthlyBoardSummary />}
              />
              <Route
                path="/reports/board-pack"
                element={<BoardPack />}
              />
              {/* The board pack's record sections under ui_uplift next (Schedule). Static, so it ranks
                  above the /reports/:reportKey/:section catch-all; classic shows the pack there. */}
              <Route
                path="/reports/board-pack/:section"
                element={<BoardPack />}
              />
              {/* Phone-first board packet reader (frames 5g/5h). Static segment,
                  so it ranks above the /reports/:reportKey catch-all. */}
              <Route
                path="/reports/statements"
                element={<StatementReaderPage />}
              />
              {/* Each statement's tab under ui_uplift next (#6840). Static, so it ranks above the
                  /reports/:reportKey/:section catch-all; classic shows the packet there. */}
              <Route
                path="/reports/statements/:statement"
                element={<StatementReaderPage />}
              />
              {/* Batch metadata has a dedicated selector; keep the legacy report URL a stable alias. */}
              <Route
                path="/reports/batch-contribution-summary"
                element={<Navigate to="/batch-report" replace />}
              />
              <Route
                path="/reports/:reportKey/print"
                element={<ReportPrintView />}
              />
            </Route>
            {/* The report record, behind the same gate. `recordRoute`: under ui_uplift next a
                denied viewer gets the record's T2 no-permission state. The `:section` routes are
                that layout's tabs (Schedule, History); classic redirects them to the report. */}
            <Route element={<ReportsAccessGate recordRoute />}>
              <Route
                path="/reports/:reportKey"
                element={<ReportRunner />}
              />
              <Route
                path="/reports/:reportKey/:section"
                element={<ReportRunner />}
              />
            </Route>
            <Route
              path="/requests"
              element={<RequestsRouter />}
            />
            <Route
              path="/requests/new"
              element={<RequestCreateRedirect />}
            />
            <Route
              path="/operations/requests"
              element={(
                <ModuleRouteGate module="requests">
                  <RequestsRouter defaultLane="operations" />
                </ModuleRouteGate>
              )}
            />
            {/* Sprint 3G: Legacy URL redirects */}
            <Route
              path="/ops-requests"
              element={<Navigate to="/operations/requests" replace />}
            />
            <Route
              path="/requests/:requestId"
              element={<RequestRedirect />}
            />
            <Route
              path="/requests/:requestId/:section"
              element={<RequestRedirect />}
            />
            <Route
              path="/projects"
              element={<TierRouteGate module="projects_tasks"><Projects /></TierRouteGate>}
            />
            <Route
              path="/projects/:projectId"
              element={<TierRouteGate module="projects_tasks"><ProjectDetail /></TierRouteGate>}
            />
            <Route
              path="/events"
              element={<Calendar />}
            />
            <Route
              path="/events/:eventId"
              element={<EventDetail />}
            />
            {/* The next record's tabs are routes (/events/:id/roster); classic redirects them to
                the record. Static paths below (templates, waitlist, check-in) still win. */}
            <Route
              path="/events/:eventId/:tab"
              element={<EventDetail />}
            />
            <Route
              path="/events/templates"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <EventTemplates />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/waitlist"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <WaitlistManager />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* /events/check-in + .../kiosk live under the staff-or-volunteer
                area (below) so a volunteer can man a check-in station. */}
            {/* Legacy /calendar redirects → /events */}
            <Route path="/calendar" element={<Navigate to="/events" replace />} />
            <Route path="/calendar/:eventId" element={<NavigateCalendarEvent />} />
            <Route
              path="/room-bookings"
              element={<RoomBookings />}
            />
            <Route
              path="/attendance"
              element={<Attendance />}
            />
            <Route
              path={KIDS_CHECK_IN_PATH}
              element={<TierRouteGate module="kids_checkin"><KidsCheckIn /></TierRouteGate>}
            />
            <Route
              path="/schools"
              element={<SchoolChildcareRoute><SchoolsHomePage /></SchoolChildcareRoute>}
            />
            <Route
              path="/schools/academics"
              element={<SchoolAcademicRoute><SchoolsAcademicsPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/gradebook"
              element={<SchoolAcademicRoute><SchoolsGradebookPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/report-cards"
              element={<SchoolAcademicRoute><SchoolsReportCardsPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/reports/diocesan"
              element={<SchoolAcademicRoute><SchoolsReportExportsPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/reports/accreditation"
              element={<SchoolAcademicRoute><SchoolsReportExportsPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/transcripts"
              element={<SchoolAcademicRoute><SchoolsTranscriptsPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/sacraments"
              element={<SchoolAcademicRoute><SchoolsSacramentsPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/service-hours"
              element={<SchoolAcademicRoute><SchoolsServiceHoursPage /></SchoolAcademicRoute>}
            />
            <Route
              path="/schools/admissions"
              element={<SchoolChildcareRoute><SchoolsAdmissionsPage /></SchoolChildcareRoute>}
            />
            <Route
              path="/schools/tuition"
              element={<SchoolFinanceRoute scope="fee-schedules"><SchoolsTuitionPage /></SchoolFinanceRoute>}
            />
            <Route
              path="/schools/enrollments"
              element={<SchoolFinanceRoute><SchoolsEnrollmentsPage /></SchoolFinanceRoute>}
            />
            <Route
              path="/schools/contracts"
              element={<SchoolFinanceRoute><SchoolsContractsPage /></SchoolFinanceRoute>}
            />
            <Route
              path="/schools/billing"
              element={<SchoolFinanceRoute><SchoolsBillingPage /></SchoolFinanceRoute>}
            />
            <Route
              path="/schools/autopay"
              element={<SchoolFinanceRoute><SchoolsAutopayPage /></SchoolFinanceRoute>}
            />
            <Route
              path="/schools/discounts-aid"
              element={<SchoolFinanceRoute><SchoolsDiscountsAidPage /></SchoolFinanceRoute>}
            />
            <Route
              path="/schools/roster"
              element={<SchoolChildcareRoute><SchoolsRosterPage /></SchoolChildcareRoute>}
            />
            <Route
              path="/schools/daily-reports"
              element={<SchoolAttendanceRoute><SchoolsDailyReportsPage /></SchoolAttendanceRoute>}
            />
            <Route
              path="/schools/health"
              element={<SchoolHealthRoute><SchoolsHealthPage /></SchoolHealthRoute>}
            />
            <Route
              path="/schools/attendance"
              element={<SchoolAttendanceRoute><SchoolsAttendancePage /></SchoolAttendanceRoute>}
            />
            <Route
              path="/schools/chapel-attendance"
              element={<SchoolAttendanceRoute><SchoolsChapelAttendancePage /></SchoolAttendanceRoute>}
            />
            {/* O6: /services + /services/:serviceId moved to the staff-or-volunteer
                block above (read-only for volunteers). /live stays staff-only —
                running a live service is a staff write surface. */}
            <Route
              path="/services/:serviceId/live"
              element={<Suspense fallback={<ForgePageLoader />}><ServiceLive /></Suspense>}
            />
            <Route
              path="/songs"
              element={<SongLibrary />}
            />
            <Route
              path="/sermons"
              element={
                <StaffRoute>
                  <CapabilityRouteGate capability="ministry.sermons">
                    <Suspense fallback={<ForgePageLoader />}>
                      <Sermons />
                    </Suspense>
                  </CapabilityRouteGate>
                </StaffRoute>
              }
            />
            <Route
              path="/volunteers"
              element={<TierRouteGate module="volunteer_scheduling"><Volunteers /></TierRouteGate>}
            />
            <Route
              path="/volunteers/background-checks"
              element={
                <TierRouteGate module="volunteer_scheduling">
                  <ProtectedRoute>
                    <Suspense fallback={<ForgePageLoader />}>
                      <BackgroundChecks />
                    </Suspense>
                  </ProtectedRoute>
                </TierRouteGate>
              }
            />
            <Route
              path="/volunteers/certifications"
              element={
                <TierRouteGate module="volunteer_scheduling">
                  <ProtectedRoute>
                    <Suspense fallback={<ForgePageLoader />}>
                      <Certifications />
                    </Suspense>
                  </ProtectedRoute>
                </TierRouteGate>
              }
            />
            <Route
              path="/volunteers/onboarding"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <VolunteerOnboarding />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/volunteers/hours"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <VolunteerHours />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/serving-teams"
              element={<TierRouteGate module="volunteer_scheduling"><ServingTeams /></TierRouteGate>}
            />
            <Route
              path="/serving-teams/:teamId"
              element={<TierRouteGate module="volunteer_scheduling"><ServingTeamDetail /></TierRouteGate>}
            />
            <Route path="/ministry" element={<MinistryHub />} />
            <Route
              path="/ministries"
              element={<Navigate to="/settings/ministries" replace />}
            />
            <Route
              path="/ministries/:ministryId"
              element={<MinistryDetail />}
            />
            <Route
              path="/ministries/:ministryId/:tab"
              element={<MinistryDetail />}
            />
            <Route
              path="/pastoral-care"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <PastoralCare />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pastoral-care/:requestId"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <PastoralCareDetail />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* A care request's tabs under ui_uplift = next (/pastoral-care/:id/notes); the
                classic page sends these back to the request. */}
            <Route
              path="/pastoral-care/:requestId/:section"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <PastoralCareDetail />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* PotentialDuplicates.tsx deprecated in favor of the fuller
                CommunityMerge tool (#6365) — old links/bookmarks redirect. */}
            <Route path="/people/duplicates" element={<Navigate to="/community/merge" replace />} />
            <Route
              path="/groups/finder"
              element={
                <TierRouteGate module="groups">
                  <ProtectedRoute>
                    <Suspense fallback={<ForgePageLoader />}>
                      <GroupFinder />
                    </Suspense>
                  </ProtectedRoute>
                </TierRouteGate>
              }
            />
            <Route
              path="/groups/management"
              element={
                <OrgAdminRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <GroupManagement />
                  </Suspense>
                </OrgAdminRoute>
              }
            />
            <Route
              path="/groups/curriculum"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <CurriculumManagement />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/health"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <GroupHealthDashboard />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/communication"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <GroupCommunication />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/volunteer-dashboard"
              element={<VolunteerDashboard />}
            />
            <Route
              path="/today"
              element={
                <StaffRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <StaffToday />
                  </Suspense>
                </StaffRoute>
              }
            />
            <Route path="/staff-today" element={<Navigate to="/today" replace />} />
            <Route
              path="/messaging"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <Messaging />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/messaging/sent"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <SentMessages />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/messaging/health"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <MessagingHealth />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/messaging/templates"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <CommunicationTemplates />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/messaging/articles"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <Articles />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications/drip-campaigns"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <DripCampaigns />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications/broadcast-calendar"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <BroadcastCalendar />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications/ab-testing"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<ForgePageLoader />}>
                    <ABTesting />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications/sms-usage"
              element={
                <TierRouteGate module="sms_messaging">
                  <ProtectedRoute>
                    <Suspense fallback={<ForgePageLoader />}>
                      <SmsUsageDashboard />
                    </Suspense>
                  </ProtectedRoute>
                </TierRouteGate>
              }
            />
            <Route
              path="/direct-messages"
              element={<DirectMessages />}
            />
            <Route
              path="/notifications"
              element={<Notifications />}
            />
            <Route
              path="/announcements"
              element={
                <StaffRoute>
                  <Announcements />
                </StaffRoute>
              }
            />
            <Route
              path="/announcements/new"
              element={
                <StaffRoute>
                  <AnnouncementNew />
                </StaffRoute>
              }
            />
            <Route
              path="/announcements/:id"
              element={
                <StaffRoute>
                  <AnnouncementDetail />
                </StaffRoute>
              }
            />
            <Route
              path="/announcements/:id/:section"
              element={
                <StaffRoute>
                  <AnnouncementDetail />
                </StaffRoute>
              }
            />
            <Route
              path="/bulletins"
              element={
                <StaffRoute>
                  <BulletinsList />
                </StaffRoute>
              }
            />
            <Route
              path="/bulletins/new"
              element={
                <StaffRoute>
                  <BulletinBuilderPage />
                </StaffRoute>
              }
            />
            <Route
              path="/bulletins/:id"
              element={
                <StaffRoute>
                  <BulletinBuilderPage />
                </StaffRoute>
              }
            />
            <Route
              path="/bulletins/:id/view"
              element={
                <StaffRoute>
                  <BulletinDetailView />
                </StaffRoute>
              }
            />
            <Route
              path="/bulletins/:id/print"
              element={
                <StaffRoute>
                  <BulletinPrintView />
                </StaffRoute>
              }
            />
            <Route
              path="/bulletins/:id/insert"
              element={
                <StaffRoute>
                  <BulletinDuplicateInsert />
                </StaffRoute>
              }
            />
            <Route
              path="/assets"
              element={<TierRouteGate module="asset_management"><Assets /></TierRouteGate>}
            />
            <Route
              path="/integrations"
              element={
                <OrgAdminRoute>
                  <Integrations />
                </OrgAdminRoute>
              }
            />
            <Route
              path="/integrations/providers/:providerId"
              element={<OrgAdminRoute><Integrations recordOnly /></OrgAdminRoute>}
            />
            <Route
              path="/integrations/stripe"
              element={
                <OrgAdminRoute>
                  <StripeIntegration />
                </OrgAdminRoute>
              }
            />
            {/*
              Authenticated, but deliberately NOT OrgAdminRoute. This is the
              Stripe Account Link return target (Express and Standard alike,
              #6151): the `org` query param names the organization the flow
              was for, not whichever org happens to be active in this tab. A
              multi-org admin who switched the active org in another tab
              while Stripe was open would otherwise fail the org-admin gate,
              the component would never mount, and the admin would be
              stranded on Stripe's own return page with no way back into
              Forge. Authorization is not weakened by this: the page itself
              performs no privileged action (it only reads query params and
              redirects/toasts), and the edge functions that DID create the
              account and the Account Link (stripe-connect-*-onboard/-refresh)
              already re-validated owner/admin against that organization_id
              before this route was ever reached.
            */}
            <Route
              path="/integrations/stripe-connect"
              element={
                <ProtectedRoute>
                  <StripeConnectCallback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations/callback"
              element={
                <OrgAdminRoute>
                  <IntegrationCallback />
                </OrgAdminRoute>
              }
            />
            <Route
              path="/settings"
              element={<SettingsIndexRedirect />}
            />
            <Route
              path="/settings/:section"
              element={(
                // Classic: SettingsLayout. ui_uplift next: SettingsPage draws the T5 settings nav (#6736).
                <SettingsSectionLayout>
                  <SettingsPageLazy />
                </SettingsSectionLayout>
              )}
            />
            <Route
              path="/settings/setup"
              element={
                <OrgAdminRoute>
                  <SetupRoute />
                </OrgAdminRoute>
              }
            />
            <Route
              path="/settings/directory"
              element={
                <OrgAdminRoute>
                  <SettingsLayout>
                    <DirectoryAdminSettings embedded />
                  </SettingsLayout>
                </OrgAdminRoute>
              }
            />
            <Route
              path="/settings/integrations"
              element={
                <OrgAdminRoute>
                  <SettingsLayout>
                    <Integrations embedded />
                  </SettingsLayout>
                </OrgAdminRoute>
              }
            />
            <Route
              path="/settings/integrations/providers/:providerId"
              element={<OrgAdminRoute><SettingsLayout><Integrations embedded recordOnly /></SettingsLayout></OrgAdminRoute>}
            />
            <Route
              path="/settings/integrations/callback"
              element={
                <OrgAdminRoute>
                  <IntegrationCallback />
                </OrgAdminRoute>
              }
            />
            <Route
              path="/settings/integrations/:integrationId"
              element={
                <OrgAdminRoute>
                  <IntegrationSettings />
                </OrgAdminRoute>
              }
            />
            <Route
              path="/settings/integrations/email-setup"
              element={
                <OrgAdminRoute>
                  <EmailDomainSetup />
                </OrgAdminRoute>
              }
            />
          </Route>
          
          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AuthenticatedAdminRoute>
                <AdminDashboard />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/requests"
            element={
              <AuthenticatedAdminRoute>
                <AdminRequests />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/requests/:requestId"
            element={
              <AuthenticatedAdminRoute>
                <AdminRequestDetail />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <AuthenticatedAdminRoute>
                <AdminOrganizations />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/organizations/:orgId"
            element={
              <AuthenticatedAdminRoute>
                <AdminOrganizationDetail />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AuthenticatedAdminRoute>
                <AdminUsers />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <AuthenticatedAdminRoute>
                <AdminUserDetail />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/search-analytics"
            element={
              <AuthenticatedAdminRoute>
                <AdminSearchAnalytics />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <AuthenticatedAdminRoute>
                <AdminAuditLogs />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/observability"
            element={
              <AuthenticatedAdminRoute>
                <AdminObservability />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/workflows"
            element={
              <AuthenticatedAdminRoute>
                <AdminWorkflowExecutions />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <AuthenticatedAdminRoute>
                <AdminBilling />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/support-sessions"
            element={
              <AuthenticatedAdminRoute>
                <AdminSupportSessions />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/telemetry"
            element={
              <AuthenticatedAdminRoute>
                <AdminTelemetry />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/communications"
            element={
              <AuthenticatedAdminRoute>
                <AdminCommunications />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/impersonation"
            element={<Navigate to="/admin/support-sessions" replace />}
          />
          <Route
            path="/admin/integrations/zoho"
            element={
              <AuthenticatedAdminRoute>
                <AdminZohoIntegration />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              <AuthenticatedAdminRoute>
                <AdminFeatureFlags />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/ai-model-settings"
            element={
              <AuthenticatedAdminRoute>
                <AdminAIModelSettings />
              </AuthenticatedAdminRoute>
            }
          />
          <Route
            path="/admin/job-runner"
            element={
              <AuthenticatedAdminRoute>
                <JobRunner />
              </AuthenticatedAdminRoute>
            }
          />

              {/* Disabled account */}
              <Route
                path="/disabled"
                element={
                  <AuthenticatedRoute>
                    <AccountDisabled />
                  </AuthenticatedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
              </Route>
              </Routes>
            </Suspense>
          </AppShell>
        </GlobalErrorBoundary>
        </UserScopedQueryClientProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
);

export default App;
